import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { AppNotification } from '../hooks/useAnalysisJobs';

interface Props {
  toasts: AppNotification[];
  onOpen: (notif: AppNotification) => void;
  onDismiss: (id: string) => void;
}

const AUTO_DISMISS_MS = 5000;
const EXIT_MS = 300;

function ToastItem({ toast, onOpen, onDismiss }: { toast: AppNotification; onOpen: (n: AppNotification) => void; onDismiss: (id: string) => void }) {
  const [visible, setVisible] = useState(false);
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const close = () => {
    setVisible(false);
    exitTimer.current = setTimeout(() => onDismiss(toast.id), EXIT_MS);
  };

  useEffect(() => {
    const enter = setTimeout(() => setVisible(true), 20);       // slide-in (fade bas→haut)
    const auto = setTimeout(() => close(), AUTO_DISMISS_MS);    // auto-dismiss
    return () => {
      clearTimeout(enter);
      clearTimeout(auto);
      if (exitTimer.current) clearTimeout(exitTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isError = toast.kind === 'analysis_error';

  return (
    <div
      onClick={() => { onOpen(toast); onDismiss(toast.id); }}
      className={`pointer-events-auto w-80 max-w-[calc(100vw-2rem)] cursor-pointer rounded-2xl border-2 px-4 py-3 flex items-start gap-3 transition-all duration-300 ease-out
        bg-cream-app border-ink
        ${visible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}
    >
      <span className={`mt-0.5 grid place-items-center w-7 h-7 rounded-full shrink-0 ${isError ? 'bg-red-300 text-ink' : 'bg-emerald-300 text-ink'}`}>
        {isError ? (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
        ) : (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-900 leading-snug">
          {isError ? 'Analyse échouée' : 'Analyse prête'}
        </p>
        <p className="text-xs text-slate-500 leading-snug mt-0.5 line-clamp-2">{toast.message}</p>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); close(); }}
        className="shrink-0 p-1 -mr-1 -mt-1 rounded text-slate-300 hover:text-slate-600 transition-colors"
        title="Fermer"
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
      </button>
    </div>
  );
}

/** Toasts transitoires (bas-droite) annonçant la fin d'une analyse. */
export default function AnalysisToast({ toasts, onOpen, onDismiss }: Props) {
  if (toasts.length === 0) return null;
  return createPortal(
    <div className="fixed bottom-4 right-4 z-[90] flex flex-col-reverse gap-2 pointer-events-none">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onOpen={onOpen} onDismiss={onDismiss} />
      ))}
    </div>,
    document.body,
  );
}
